// §4.2 — CUSTOMER 360, /sales/customers/$customerId (Module 09, P3-A part 2).
//
// ONE GET. `Customer360Response` carries the customer, contacts, channels,
// assignments, latest reliability snapshot, distributor products and the open
// AR state — so the header AND every shipped tab render off a single fetch,
// which is what meets the spec's <2 s mobile pull-up budget. The only other
// reads are deliberately off the critical path: the owner-name index is
// parallel and non-blocking (RefLabel short-id until it lands), the
// reliability HISTORY fetches only when the formula popover opens, and the
// Pricing tab's effective-list read fires only when that tab is opened.
//
// WHAT THE TABS DO NOT PRETEND (D-033, stated in the §4.2 annotations):
// the Activity tab arrives with the slice that gives it data, and the
// Documents tab ARRIVED with E2-2a2 (below).
// The open-orders panel ARRIVED with E2-1 as the Latest orders panel. The Telegram connect link ARRIVED
// with module 17 (P3-B part 2): the Contacts tab's cards embed the §4.5 link
// widget. The quote panel ARRIVED with P3-C-1, and the per-customer override
// list with P3-C-2 (D-263): the Pricing tab's panel is a lazy read like the
// effective list beside it, and the §4.6 inbox deep-links to it through the
// one URL param this screen reads (`?tab=pricing`, search-params.ts).
//
// REDACTION IS OMISSION (§7): credit limit/terms render only when their keys
// are present; the reliability formula popover is offered only when the
// snapshot's formula inputs are; `open_ar.available === false` renders the
// module-11 chip — a typed fact, client-localized (D-225).
//
// E2-1 (P3-E2 PR E2-1, spec 09 §4.2, D-313 ruling 9). The header shows the
// customer's next step or the discipline flag (served on the one GET as
// `next_activity` and `needs_next_step`, §3.12) and, for a reader who can open
// the trail, the audit trail link. The Overview gains three lazy panels, each
// with its own one-line failure: Tasks (the open-task card with a customer
// subject and the latest five done, shown to holders of
// `sales.activity.write`), Deals and Latest orders. The sentence that said
// orders and the activity history were not shown here yet is gone, because
// both now are.
//
// THE OVERVIEW PAINTS IN TWO PARTS (spec 09 §4.2, E2-1 wave 2). The open AR
// and primary-contact cards come from the one GET and paint with the header.
// The panel grid after them (tasks, deals, quotes, orders and, because it
// sits after them, the assignments) waits behind one reserving card per panel
// until every panel read has answered, failed or paused offline, then
// reveals at once. The cards painted first sit ahead of the grid, so the
// reveal moves none of them.
//
// E2-2a1 (P3-E2 PR E2-2a1, spec 09 §4.2, D-323 rulings 1 to 4). The header
// shows the pinned note under the customer's name, with its author and date or
// "carried over", and the customer's tags as chips, both from the one GET
// (`pinned_note`, `customer.tags`), so they paint with the header. A writer
// adds and removes tags there, which is how a phone reader tags a customer:
// the book offers no bulk bar below 769 px. The Overview gains the Notes
// panel, a lazy read started with the panels' reads. It joins the grid's
// reveal (one more reserving card, full width) and sits just before the Tasks
// card, which spec 09 §4.2 keeps last, so neither its rows arriving nor the
// grid revealing moves a card the reader has already seen.
//
// E2-2b (P3-E2 PR E2-2b, spec 09 §4.2's E2-2b annotation, D-324 rulings 14 to
// 16). An admin sees Merge on an active customer, which opens §4.17, and
// Restore on an archived customer that was never merged (`canMergeCustomers`,
// `canRestoreCustomer`: the archive's admin lineage, offered on the literal
// roles as Archive is). Below 769 px Merge says "Merging two customers needs a
// wider screen" under the header and opens nothing, because §4.17 is desktop
// only; the tombstone and Restore work on a phone. A MERGED customer's 360 is
// the tombstone: it reads "Merged into X on ..." with X from
// `merged_into_display_name` and the date from `merged_at`, a link to the
// winner only when `merged_into_openable` holds (§4.1's rule, so a reader the
// winner's door refuses never follows a link to a 404), and it offers no edit
// verb. Everything the merge could move now sits on the winner, so the
// tombstone offers no credit toggle, no assignment editing, no task card and
// no distributor-list editing; the panels that read stay, and read what the
// merge left (§3.13's stays). A restore refused 409 `CUSTOMER_MERGED` (the
// customer was merged after this 360 was read) names the winner, and the 360
// is read again, so the tombstone replaces the archived header.
//
// E2-2a2 (P3-E2 PR E2-2a2, spec 09 §4.2's E2-2a2 annotation, D-323 ruling 14).
// The Documents tab lands: the customer's quotes and orders as one timeline
// with their PDF doors and each order's Shipments panel, and the customer's
// Attached files with Upload and Detach for a writer of the record
// (components/attachments/documents-tab.tsx). Both reads start only when the
// tab is opened, so the 360's first paint still costs one GET.

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { PageHeader } from "../../../../components/breadcrumbs";
import { CheckboxField } from "../../../../components/field";
import { RefLabel } from "../../../../components/ref-label";
import { NavTabs } from "../../../../components/tabs";
import {
  CardSkeleton,
  ErrorState,
  ForbiddenState,
  OfflineState,
  Skeleton,
} from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { useDesktop } from "../../../../components/use-desktop";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { formatBusinessDate, formatTimestamp } from "../../../../lib/datetime";
import { useTenantTimezone } from "../../../../lib/role-codes";
import { reliabilityBand, reliabilityFormula } from "../lib/reliability";
import { formatMinor } from "../lib/money";
import { MutationFailureNote } from "../mutation-failure-note";
import { ContactEditor } from "../components/contact-editor";
import { DistributorProducts } from "../components/distributor-products";
import { PriceCheck } from "../components/price-check";
import { AssignmentsPanel } from "../components/assignments";
import { CustomerQuotesPanel } from "../components/customer-quotes";
import {
  CustomerDealsPanel,
  CustomerOrdersPanel,
  overviewPanelSlots,
  useOverviewSettled,
} from "../components/customer-overview-panels";
import { NextStepCell } from "../components/pipeline/pipeline-parts";
import { OpenTasksCard } from "../components/sales-tasks";
import { nextStepOf } from "../lib/pipeline";
import { readSettled, useMemberRefAsk, useReadsHeldOffline, useRevealLatch } from "../lib/reads";
import { AuditTrailLink } from "../../audit/components/audit-trail-link";
import { CustomerOverridesPanel } from "../components/customer-overrides";
import { PinnedNote } from "../components/notes/pinned-note";
import { NotesPanel } from "../components/notes/notes-panel";
import { CustomerTags } from "../components/tags/customer-tags";
import { DocumentsTab } from "../components/attachments/documents-tab";
import { useCustomerNotes } from "../hooks/crm-queries";
import { useRestoreCustomer } from "../hooks/merge-queries";
import {
  canBulkArchive,
  canMergeCustomers,
  canRestoreCustomer,
  isSalesReadRefused,
  useSalesPermissions,
} from "../hooks/permissions";
import {
  useArchiveCustomer,
  useCustomer360,
  useOwnerIndex,
  usePriceLists,
  useReliabilityHistory,
  useUpdateCustomer,
  type Customer360,
} from "../hooks/sales-queries";
import { tierChipVariant } from "./customers";
import { validateSalesCustomer360Search, type Customer360Tab } from "./search-params";
import "../components/merge/merge.css";

// The Documents tab (E2-2a2) is opened from the strip and is not one of the
// address's tabs: `?tab=` stays the §4.6 inbox's deep link into Pricing
// (search-params.ts), and a foreign value still opens the Overview.
type TabKey = Customer360Tab | "documents";

export function SalesCustomer360Screen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const perms = useSalesPermissions();
  const params = useParams({ strict: false });
  const customerId = typeof params.customerId === "string" ? params.customerId : "";

  const refused = isSalesReadRefused(perms, perms.canOpenCustomers && perms.has("sales.read"));
  const card = useCustomer360(refused ? "" : customerId);
  // The one URL param (P3-C-2): a `price_override` card in the §4.6 inbox
  // lands the reviewer on the Pricing tab. Re-validated defensively — a
  // foreign value opens the Overview — and re-applied when the address
  // changes under a mounted screen (inbox → customer A → customer B).
  const rawSearch = useSearch({ strict: false });
  const urlTab = validateSalesCustomer360Search(rawSearch).tab;
  const [tab, setTab] = useState<TabKey>(urlTab ?? "overview");
  useEffect(() => {
    if (urlTab !== undefined) setTab(urlTab);
  }, [urlTab, customerId]);

  if (refused) {
    return <ForbiddenState title={t("sales.access.title")} body={t("sales.access.customers")} />;
  }
  if (card.error instanceof UnauthorizedError) {
    void navigate({ to: "/login" });
    return null;
  }
  if (card.fetchStatus === "paused" && card.data === undefined) {
    return <OfflineState title={t("sales.offline.title")} body={t("sales.offline.body")} />;
  }
  if (card.isPending) {
    return <CardSkeleton lines={8} label={t("sales.common.loading")} />;
  }
  if (card.error !== null) {
    return (
      <ErrorState
        title={t("sales.c360.error")}
        retryLabel={t("sales.common.retry")}
        requestIdLabel={t("sales.common.requestId")}
        requestId={requestIdOf(card.error)}
        onRetry={() => {
          void card.refetch();
        }}
      />
    );
  }

  return <Customer360Body data={card.data} tab={tab} onTab={setTab} />;
}

function Customer360Body({
  data,
  tab,
  onTab,
}: {
  data: Customer360;
  tab: TabKey;
  onTab: (tab: TabKey) => void;
}) {
  const { t, i18n } = useTranslation();
  const perms = useSalesPermissions();
  const customer = data.customer;
  const owners = useOwnerIndex(perms.isAdmin);
  const update = useUpdateCustomer();
  const archive = useArchiveCustomer();
  const restore = useRestoreCustomer();
  const navigate = useNavigate();
  const desktop = useDesktop();
  // §4.17 is desktop only: on a phone Merge says so and opens nothing.
  const [mergeNarrow, setMergeNarrow] = useState(false);
  const merged = isMerged(customer);
  const mayMerge = canMergeCustomers(perms, customer);
  const mayRestore = canRestoreCustomer(perms, customer);
  const mayArchive = canBulkArchive(perms) && customer.status === "active";

  return (
    <section className="sales-360-screen" data-testid="sales-360-screen">
      <PageHeader
        title={customer.display_name}
        crumbs={[{ label: t("sales.c360.crumbRoot"), to: "/sales/customers" }]}
        data-testid="sales-360-pagehead"
        actions={
          mayMerge || mayRestore || mayArchive ? (
            <>
              {mayMerge ? (
                <Button
                  size="sm"
                  data-testid="sales-360-merge"
                  onClick={() => {
                    if (desktop) {
                      void navigate({
                        to: "/sales/customers/$customerId/merge",
                        params: { customerId: customer.id },
                      });
                    } else {
                      setMergeNarrow(true);
                    }
                  }}
                >
                  {t("sales.merge.open")}
                </Button>
              ) : null}
              {mayRestore ? (
                <Button
                  size="sm"
                  data-testid="sales-360-restore"
                  busy={restore.isPending}
                  busyLabel={t("sales.merge.restoring")}
                  onClick={() => {
                    restore.mutate(customer.id);
                  }}
                >
                  {t("sales.merge.restore")}
                </Button>
              ) : null}
              {mayArchive ? (
                <Button
                  variant="danger"
                  size="sm"
                  data-testid="sales-360-archive"
                  busy={archive.isPending}
                  busyLabel={t("sales.c360.archiving")}
                  onClick={() => {
                    archive.mutate(customer.id);
                  }}
                >
                  {t("sales.c360.archive")}
                </Button>
              ) : null}
            </>
          ) : undefined
        }
      />

      <div className="sales-360-header" data-testid="sales-360-header">
        <div className="sales-360-idline">
          <span className="sales-360-no num">{customer.customer_no}</span>
          <StatusChip variant={tierChipVariant(customer.tier)} dot={false}>
            {t(`sales.tier.${customer.tier}`)}
          </StatusChip>
          {customer.status === "archived" ? (
            <StatusChip variant="neutral" data-testid="sales-360-archived">
              {t("sales.customers.statusArchived")}
            </StatusChip>
          ) : null}
          {customer.credit_hold && !(perms.canEditCredit && !merged) ? (
            <StatusChip variant="bad">{t("sales.customers.creditHold")}</StatusChip>
          ) : null}
          <ReliabilityChip data={data} />
          <AuditTrailLink entityType="customer" entityId={customer.id} />
        </div>

        {/* D-560 ruling 3: the record's facts read as one wrapping line of
            muted labels and values, where they were four stacked rows of
            "Label: value". Each fact keeps its own test id. */}
        <div className="sales-facts-line" data-testid="sales-360-facts">
          {/* §2.1's legal name (P3-E2 PR E2-2b). The display name heads the
              page, and the legal name is the field a merge most often takes
              from the merged-in customer (§3.13), so a reader can see which
              one the record kept. M09-S29 step 3 reads it here. It is a read
              of the one GET and adds no request. */}
          <span className="sales-fact" data-testid="sales-360-legal-name">
            <span className="sales-hint">{t("sales.c360.legalName")}</span>{" "}
            <strong>{customer.legal_name}</strong>
          </span>
          {/* §7 omission-aware: the limit renders only when its key is present. */}
          {customer.credit_limit_minor !== undefined ? (
            <span className="sales-fact">
              <span className="sales-hint">{t("sales.c360.creditLimit")}</span>{" "}
              <strong className="num">
                {customer.credit_limit_minor === 0
                  ? t("sales.customers.prepayOnly")
                  : formatMinor(
                      customer.credit_limit_minor,
                      customer.default_currency,
                      i18n.language,
                    )}
              </strong>
            </span>
          ) : null}
          <span className="sales-fact">
            <span className="sales-hint">{t("sales.c360.creditTerms")}</span>{" "}
            <strong className="num">
              {t("sales.c360.termsDays", { count: customer.credit_terms_days })}
            </strong>
          </span>
          {/* §2.1's preferred language, which the documents print in and the
              customers import lane may update (§3.15; M09-S30 step 3). */}
          <span
            className="sales-fact"
            data-testid="sales-360-locale"
            data-locale={customer.preferred_locale}
          >
            <span className="sales-hint">{t("sales.c360.preferredLanguage")}</span>{" "}
            <strong>{t(`sales.c360.localeName.${customer.preferred_locale}`)}</strong>
          </span>
          {/* §3.12's next step or the flag. The step never shows a task's
              words or its assignee; the server serves it to every reader of
              the customer. The span stays when there is none and the line's
              rule hides it while it is empty. */}
          <span className="sales-fact" data-testid="sales-360-nextstep-line">
            {nextStepOf(customer.next_activity, customer.needs_next_step).kind !== "none" ? (
              <>
                <span className="sales-hint">{t("sales.c360.nextStep")}</span>{" "}
                <NextStepCell
                  next={customer.next_activity}
                  needsNextStep={customer.needs_next_step}
                />
              </>
            ) : null}
          </span>
          {perms.canEditCredit && !merged ? (
            <CheckboxField
              label={t("sales.c360.creditHold")}
              checked={customer.credit_hold}
              disabled={update.isPending}
              data-testid="sales-credit-hold-toggle"
              onChange={(checked) => {
                update.mutate({ id: customer.id, body: { credit_hold: checked } });
              }}
            />
          ) : null}
        </div>

        {/* E2-2b: the tombstone line, and the phone's answer to Merge. */}
        {merged ? <Tombstone customer={customer} /> : null}
        {mergeNarrow && !desktop ? (
          <p className="sales-hint" role="status" data-testid="sales-360-merge-narrow">
            {t("sales.merge.narrow")}
          </p>
        ) : null}

        {/* E2-2a1: the pinned note and the tags, both off the one GET. */}
        <PinnedNote note={data.pinned_note} />
        <CustomerTags
          customerId={customer.id}
          customerActive={customer.status === "active"}
          tags={customer.tags}
        />

        {update.isError ? (
          <MutationFailureNote
            data-testid="sales-mutation-failed"
            text={t("sales.c360.creditFailed")}
            error={update.error}
          />
        ) : null}
        {archive.isError ? (
          <MutationFailureNote
            data-testid="sales-mutation-failed"
            text={t("sales.c360.archiveFailed")}
            error={archive.error}
          />
        ) : null}
        {restore.isError ? (
          <MutationFailureNote
            data-testid="sales-360-restore-failed"
            text={restoreRefusalText(restore.error, customer, t)}
            error={restore.error}
          />
        ) : null}
      </div>

      <NavTabs
        className="sales-360-tabstrip"
        label={t("sales.c360.tabsAria")}
        data-testid="sales-360-tabs"
        active={tab}
        onSelect={(next) => {
          onTab(next as TabKey);
        }}
        tabs={(["overview", "contacts", "pricing", "documents"] as const).map((key) => ({
          id: key,
          label: t(`sales.c360.tabs.${key}`),
          "data-testid": `sales-360-tab-${key}`,
        }))}
      />

      {tab === "overview" ? <OverviewTab data={data} owners={owners} /> : null}
      {tab === "contacts" ? (
        <div data-testid="sales-360-contacts">
          <ContactEditor
            customerId={customer.id}
            customerStatus={customer.status}
            contacts={data.contacts}
            channels={data.channels}
          />
        </div>
      ) : null}
      {tab === "pricing" ? <PricingTab data={data} /> : null}
      {/* E2-2a2 (spec 09 §4.2): the quotes, orders and attached files, read
          only when the tab is opened, off the 360's one-GET budget. */}
      {tab === "documents" ? <DocumentsTab data={data} /> : null}
    </section>
  );
}

/** The §3.9 chip with its formula popover. The popover is OFFERED only when
 * the snapshot's formula inputs are on the wire (§7 omits them otherwise);
 * the history read fires only when the popover opens. */
function ReliabilityChip({ data }: { data: Customer360 }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const snapshot = data.latest_reliability;
  const history = useReliabilityHistory(data.customer.id, open);

  if (snapshot === null) {
    return data.customer.reliability_score === null ? (
      <span className="sales-360-fact">{t("sales.c360.reliabilityNone")}</span>
    ) : (
      <StatusChip
        variant={reliabilityBand(data.customer.reliability_score)}
        data-testid="sales-reliability-chip"
        data-band={reliabilityBand(data.customer.reliability_score)}
        data-provisional={data.customer.reliability_provisional ? "true" : "false"}
      >
        {data.customer.reliability_score}
        {data.customer.reliability_provisional ? (
          <span className="sales-provisional">{t("sales.customers.provisional")}</span>
        ) : null}
      </StatusChip>
    );
  }

  const formula = reliabilityFormula(snapshot);
  return (
    <span className="sales-reliability">
      <StatusChip
        variant={reliabilityBand(snapshot.score)}
        data-testid="sales-reliability-chip"
        data-band={reliabilityBand(snapshot.score)}
        data-provisional={snapshot.provisional ? "true" : "false"}
      >
        {snapshot.score}
        {snapshot.provisional ? (
          <span className="sales-provisional">{t("sales.customers.provisional")}</span>
        ) : null}
      </StatusChip>
      {formula !== undefined ? (
        <Button
          size="sm"
          data-testid="sales-reliability-formula"
          aria-expanded={open}
          onClick={() => {
            setOpen(!open);
          }}
        >
          {open ? t("sales.common.close") : t("sales.c360.formula")}
        </Button>
      ) : null}
      {open && formula !== undefined ? (
        <span className="sales-reliability-pop" data-testid="sales-reliability-pop">
          {t("sales.c360.reliabilityFormula", {
            onTime: formula.onTime,
            paid: formula.paid,
            score: formula.score,
          })}
          {history.data !== undefined && history.data.length > 0 ? (
            <span className="sales-reliability-hist">
              {history.data.map((row) => (
                <span key={row.id} className="num">
                  {formatBusinessDate(row.as_of_date, i18n.language)} → {row.score}
                </span>
              ))}
            </span>
          ) : null}
        </span>
      ) : null}
    </span>
  );
}

function OverviewTab({
  data,
  owners,
}: {
  data: Customer360;
  owners: { byId: Map<string, string>; isPending: boolean };
}) {
  const { t, i18n } = useTranslation();
  const perms = useSalesPermissions();
  const customer = data.customer;
  const primary = data.contacts.find((c) => c.is_primary && c.status === "active");
  // The one-GET block (open AR and the primary contact) paints with the
  // header, and only the panel grid after it waits: the panels' reads decide
  // their heights, so they reveal together behind one reserving card per
  // panel (customer-overview-panels.tsx). The block comes first in the grid,
  // so the reveal never moves it. The reveal latches for this customer: a
  // panel read asked again after it (a failed read refetched on focus, the
  // network returning) renders in that panel's own line and never takes the
  // grid back to its reserving cards, or an open dialog with it.
  // E2-2a1: the notes read starts with the panels' reads, and the grid waits
  // for it too, so the Notes panel reveals with the rest.
  const notes = useCustomerNotes(customer.id);
  const panelsSettled = useOverviewSettled(customer.id);
  const settled = useRevealLatch(panelsSettled && readSettled(notes), customer.id);
  // While the browser holds an admin's directory read offline, the owner
  // reads as its short id rather than a resolving mark for the whole cut.
  const offline = useReadsHeldOffline();
  const memberRef = useMemberRefAsk(owners.isPending && !offline);
  // The tasks card is the grid's last panel and spans its width. A merged
  // customer's tasks all moved to the winner (§3.13) and a task create on it
  // answers 422 naming the winner, so the tombstone shows no tasks card and
  // reserves none, which keeps the reveal from moving a card (E2-2b).
  const merged = isMerged(customer);
  const writesTasks = perms.has("sales.activity.write");
  const tasksSlot = writesTasks && !merged;
  const panelSlots = overviewPanelSlots(perms) - (writesTasks && merged ? 1 : 0);

  return (
    <div className="sales-360-overview" data-testid="sales-360-overview">
      <div className="ds-card sales-360-card">
        <h3>{t("sales.c360.overview.arTitle")}</h3>
        {/* §3.5b — the typed degradation fact, client-localized (D-225). */}
        {data.open_ar.available ? (
          data.open_ar.open_ar_minor !== undefined && data.open_ar.currency !== undefined ? (
            <p className="num">
              {/* The app's locale, not the runtime's: `formatMinor`'s third
                  argument is optional and `undefined` resolves to the BROWSER
                  locale, so an omitted one prints this figure in a different
                  grammar from every other money value on the screen. */}
              {formatMinor(data.open_ar.open_ar_minor, data.open_ar.currency, i18n.language)}
            </p>
          ) : null
        ) : (
          <StatusChip
            variant="neutral"
            dot={false}
            className="sales-ar-chip"
            data-testid="sales-ar-chip"
          >
            {t("sales.customers.arUnavailable")}
          </StatusChip>
        )}
      </div>

      <div className="ds-card sales-360-card">
        <h3>{t("sales.c360.overview.contactTitle")}</h3>
        {primary !== undefined ? (
          <p>
            <strong>{primary.full_name}</strong>
            {primary.position !== null && primary.position.length > 0 ? (
              <span className="sales-360-fact"> · {primary.position}</span>
            ) : null}
          </p>
        ) : (
          <p className="sales-hint">{t("sales.c360.overview.noPrimary")}</p>
        )}
        <p className="sales-360-fact">
          {t("sales.c360.overview.preferredChannel")}:{" "}
          {customer.preferred_channel === "none" ? (
            <RefLabel value={undefined} id={null} />
          ) : (
            t(`sales.channel.${customer.preferred_channel}`)
          )}
        </p>
        <p className="sales-360-fact">
          {t("sales.c360.overview.owner")}:{" "}
          <RefLabel
            value={
              customer.owner_user_id === null ? undefined : owners.byId.get(customer.owner_user_id)
            }
            id={customer.owner_user_id}
            pending={owners.isPending && !offline}
            {...memberRef}
          />
        </p>
      </div>

      {settled ? (
        <>
          {/* E2-1: the customer's open deals, for the pipeline's readers. */}
          <CustomerDealsPanel
            customerId={customer.id}
            customerActive={customer.status === "active"}
          />

          {/* P3-C-1: the quote panel. */}
          <CustomerQuotesPanel customerId={customer.id} />

          {/* E2-1: the five newest orders. */}
          <CustomerOrdersPanel customerId={customer.id} />

          {/* D-241: the §2.2 assignment set — owner + collaborators, the
              people §3.4 lets send this customer's quotes. It paints from
              the one GET, and it waits with the grid because it sits after
              the panels, whose heights decide where it lands. */}
          <AssignmentsPanel
            customerId={customer.id}
            assignments={data.assignments}
            canManage={perms.has("sales.manage_customers") && !merged}
            isAdmin={perms.isAdmin}
          />

          {/* E2-2a1 (spec 09 §4.2): the notes timeline with its add box,
              full width, just before the Tasks card. */}
          <NotesPanel
            customerId={customer.id}
            customerActive={customer.status === "active"}
            notes={notes}
          />

          {/* E2-1: the customer's open tasks and the latest five done, for
              the people tasks are for (the card mounts nothing for anyone
              else). A four-column table with its verbs, so it takes the
              grid's whole width (`wide`), and it comes last: a full-width
              card between the column cards would leave the row above it
              part empty. */}
          {merged ? null : (
            <OpenTasksCard
              subject={{ kind: "customer", id: customer.id, customerId: customer.id }}
              label={customer.display_name}
              title={t("sales.c360.overview.tasksTitle")}
              errorLine={t("sales.c360.overview.tasksError")}
              emptyText={t("sales.c360.overview.tasksEmpty")}
              recentDone
              wide
            />
          )}
        </>
      ) : (
        <>
          {/* One status for the grid, and one reserving card per panel the
              reader will be shown, each in the grid cell its panel takes:
              the task writer's last card spans the grid, as the tasks card
              does. */}
          <p className="visually-hidden" role="status" data-testid="sales-360-overview-loading">
            {t("sales.common.loading")}
          </p>
          {/* E2-2a1: one more, full width, for the Notes panel, which sits
              just before the Tasks card (or last, for a reader who has none). */}
          {reservingSlots(panelSlots, tasksSlot).map((wide, slot) => (
            <div
              key={slot}
              className={wide ? "ds-card sales-360-card sales-360-wide" : "ds-card sales-360-card"}
              aria-hidden="true"
              data-testid="sales-360-panel-loading"
            >
              <Skeleton width="40%" height="1.1rem" />
              <Skeleton height="1rem" />
              <Skeleton height="1rem" />
              <Skeleton height="1rem" />
            </div>
          ))}
        </>
      )}
    </div>
  );
}

/**
 * The reserving cards the Overview grid holds while its reads settle, each
 * `true` when it spans the grid: one per column panel, then the Notes panel's
 * (full width), then the Tasks card's (full width) for a task writer. The
 * order is the revealed grid's, so each card is replaced in its own place.
 */
export function reservingSlots(panelSlots: number, tasksSlot: boolean): boolean[] {
  const columns = tasksSlot ? panelSlots - 1 : panelSlots;
  return [...Array.from({ length: columns }, () => false), true, ...(tasksSlot ? [true] : [])];
}

function PricingTab({ data }: { data: Customer360 }) {
  const { t, i18n } = useTranslation();
  const perms = useSalesPermissions();
  const customer = data.customer;
  // The effective list: the ACTIVE list for (tier, default_currency) — read
  // only when this tab is open, off the 360's critical path.
  const lists = usePriceLists(
    { tier: customer.tier, currency: customer.default_currency, status: "active" },
    undefined,
  );
  const effective = lists.data?.items[0];

  return (
    <div className="sales-360-pricing" data-testid="sales-360-pricing">
      <div className="ds-card sales-360-card">
        <h3>{t("sales.c360.pricing.effectiveTitle")}</h3>
        {lists.isPending ? (
          <p className="sales-hint">{t("sales.common.loading")}</p>
        ) : effective !== undefined ? (
          <p data-testid="sales-effective-list">
            <strong>{effective.code}</strong> · {effective.name} ·{" "}
            <span className="num">
              {formatBusinessDate(effective.valid_from, i18n.language)} –{" "}
              {effective.valid_to === null
                ? t("sales.priceLists.openEnded")
                : formatBusinessDate(effective.valid_to, i18n.language)}
            </span>
          </p>
        ) : (
          <p className="sales-hint" data-testid="sales-effective-none">
            {t("sales.c360.pricing.noEffectiveList", { currency: customer.default_currency })}
          </p>
        )}
      </div>

      {customer.tier === "distributor" ? (
        <DistributorProducts
          customerId={customer.id}
          rows={data.distributor_products}
          canManage={perms.has("sales.tier.manage") && !isMerged(customer)}
        />
      ) : null}

      <PriceCheck customer={customer} />

      {/* P3-C-2 (D-263): the §2.9 override list — P3-A's stated pending,
          landed. A lazy read like the effective list above it. */}
      <CustomerOverridesPanel customer={customer} />
    </div>
  );
}

/** Is this customer a tombstone a merge left (§2.1's `merged_into_customer_id`)? */
function isMerged(customer: Customer360["customer"]): boolean {
  return (customer.merged_into_customer_id ?? null) !== null;
}

/**
 * §4.2's tombstone line: "Merged into X on ...". X is the winner's current
 * name (`merged_into_display_name`) and links to the winner only when the
 * reader's own customer door answers it (`merged_into_openable`), so nobody
 * follows a link to a 404. The date is the merge's instant in the plant's
 * clock (D-089), the way every other wire timestamp on an office screen reads.
 */
function Tombstone({ customer }: { customer: Customer360["customer"] }) {
  const { t, i18n } = useTranslation();
  const timeZone = useTenantTimezone();
  const winnerId = customer.merged_into_customer_id ?? "";
  const name = customer.merged_into_display_name ?? t("sales.merge.anotherCustomer");
  return (
    <p
      className="sales-merge-tombstone"
      data-testid="sales-360-tombstone"
      data-winner={winnerId}
      data-openable={customer.merged_into_openable === true ? "true" : "false"}
    >
      {t("sales.merge.tombstoneLead")}{" "}
      {customer.merged_into_openable === true ? (
        <Link
          to="/sales/customers/$customerId"
          params={{ customerId: winnerId }}
          data-testid="sales-360-tombstone-link"
        >
          {name}
        </Link>
      ) : (
        <strong data-testid="sales-360-tombstone-name">{name}</strong>
      )}{" "}
      {t("sales.merge.tombstoneOn", {
        date: formatTimestamp(customer.merged_at, i18n.language, timeZone),
      })}
    </p>
  );
}

/** The restore's refusal in the reader's words: 409 `CUSTOMER_MERGED` names
 * the winner, read from the 360 the refusal made the screen read again, and
 * any other failure is the area's plain sentence (§3.14, §4.2). */
function restoreRefusalText(
  error: unknown,
  customer: Customer360["customer"],
  t: ReturnType<typeof useTranslation>["t"],
): string {
  if (error instanceof RequestError && error.code === "CUSTOMER_MERGED") {
    return t("sales.merge.restoreMerged", {
      name: customer.merged_into_display_name ?? t("sales.merge.anotherCustomer"),
    });
  }
  return t("sales.merge.restoreFailed");
}
